import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { createOrder, validateCertificate, searchCities, getBranches } from "../utils/api";
import { useToast } from "../components/ToastProvider";
import "./CheckoutPage.css";
import npImg from "../assets/images/npImg.png";
import ukrpImg1 from "../assets/images/ukrpImg1.png";
import SEO from "../components/SEO";

function BranchSelect({ provider, city, onSelect }) {
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (!city) return;

    (async () => {
      const data = await getBranches(provider, city.id);
      setBranches(data);
    })();
  }, [city, provider]);

  return (
    <select onChange={(e) => onSelect(branches[e.target.value])}>
      <option>Выберите отделение</option>
      {branches.map((b, i) => (
        <option key={b.id} value={i}>
          {b.name}
        </option>
      ))}
    </select>
  );
}

function CitySelect({ provider, onSelect }) {
  const [query, setQuery] = useState("");
  const [cities, setCities] = useState([]);

  useEffect(() => {
    if (!query) return;

    const t = setTimeout(async () => {
      try {
        const data = await searchCities(provider, query);
        setCities(data);
      } catch (e) {
        console.log("CITY SEARCH ERROR:", e);
        setCities([]);
      }
    }, 400);

    return () => clearTimeout(t);
  }, [query, provider]);

  return (
    <div>
      <input placeholder="Город" value={query} onChange={(e) => setQuery(e.target.value)} />

      {cities.length > 0 && (
        <div className="dropdown">
          {cities.map((c) => (
            <div
              key={c.id}
              onClick={() => {
                onSelect(c);
                setQuery(c.name);
                setCities([]);
              }}
            >
              {c.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CheckoutPage() {
  const { cart, totalPrice, clearCart } = useCart();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const [form, setForm] = useState({
    customerName: "",
    phone: "",
    email: "",

    delivery: {
      provider: "nova", // nova | ukr | pickup
      city: null,
      branch: null,
      addressText: "",
    },

    paymentMethod: "online", // online | prepayment | fop

    comment: "",
  });
  const [errors, setErrors] = useState({});

  // Certificate
  const [certCode, setCertCode] = useState("");
  const [certData, setCertData] = useState(null);
  const [certError, setCertError] = useState("");
  const [certLoading, setCertLoading] = useState(false);

  const discount = certData ? Math.min(parseFloat(certData.amount), totalPrice) : 0;
  const finalPrice = Math.max(0, totalPrice - discount);

  const validate = () => {
    const e = {};

    if (!form.customerName.trim()) {
      e.customerName = "Введіть ваше ім'я";
    }

    if (!form.phone.trim()) {
      e.phone = "Введіть номер телефону";
    }

    if (form.delivery.provider !== "pickup" && !form.delivery.city) {
      e.city = "Оберіть місто";
    }

    if (form.delivery.provider === "nova") {
      if (!form.delivery.branch) {
        e.branch = "Оберіть відділення";
      }
    }

    if (form.delivery.provider === "ukr") {
      if (!form.delivery.addressText?.trim()) {
        e.addressText = "Введіть адресу доставки";
      }
    }

    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) {
      e.email = "Невірний формат email";
    }

    if (form.delivery.provider === "courier") {
      if (!form.delivery.addressText?.trim()) {
        e.addressText = "Введіть адресу доставки";
      }
    }
    console.log("VALIDATION ERRORS:", e);

    return e;
  };

  const updateForm = (path, value) => {
    setForm((prev) => {
      const copy = { ...prev };

      if (path.includes(".")) {
        const [a, b] = path.split(".");
        copy[a] = { ...copy[a], [b]: value };
      } else {
        copy[path] = value;
      }

      return copy;
    });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleApplyCert = async () => {
    if (!certCode.trim()) return;
    setCertLoading(true);
    setCertError("");
    setCertData(null);
    try {
      const res = await validateCertificate(certCode.trim().toUpperCase());
      setCertData(res.data.data);
      showToast("Сертифікат застосовано! 🎁", "success");
    } catch (err) {
      setCertError(err.response?.data?.message || "Сертифікат не знайдено або недійсний");
    } finally {
      setCertLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    if (cart.length === 0) {
      showToast("Кошик порожній", "error");
      return;
    }
    console.log(
      "🔥 CART ITEMS:",
      cart.map((i) => ({
        id: i.id,
        name: i.name,
        type: i.type,
        unitPrice: i.unitPrice,
        basePrice: i.basePrice,
        price: i.price,
        quantity: i.quantity,
      }))
    );

    setSubmitting(true);
    try {
      // 1. Спочатку завжди створюємо замовлення в БД
      console.log("🔥 FORM:", form);
      const res = await createOrder({
        customerName: form.customerName,
        phone: form.phone,
        email: form.email,
        comment: form.comment,
        address:
          form.delivery.provider === "pickup"
            ? form.delivery.addressText
            : form.delivery.provider === "ukr"
              ? form.delivery.addressText
              : `${form.delivery.city?.name || ""}, ${form.delivery.branch?.name || ""}`,
        paymentMethod: form.paymentMethod,
        deliveryMethod: form.delivery.provider,
        certificate: certData ? { code: certData.code, discount } : null,
        items: cart.map((i) => {
          if (i.type === "certificate") {
            return {
              type: "certificate",
              certificateId: i.certificateId,
              quantity: i.quantity,
              price: i.unitPrice,
            };
          }
          return {
            type: "product",
            productId: i.id,
            quantity: i.quantity,
            price: Number(i.unitPrice ?? i.basePrice),
            sizeId: i.selectedSize?.id || null,
            textureId: i.selectedTexture?.id || null,
          };
        }),
      });

      const newOrderId = res.data.data.id;
      setOrderId(newOrderId);

      // 2. Тепер розгалужуємо по методу оплати
      if (form.paymentMethod === "online") {
        // Створюємо mono-рахунок
        const payRes = await fetch("/api/payment/create", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: finalPrice,
            description: "Оплата замовлення",
            orderId: String(newOrderId), // реальний ID з БД
          }),
        });
        const payData = await payRes.json();
        if (!payRes.ok) throw new Error(payData.error || "Помилка створення рахунку mono");

        // Зберігаємо для сторінки результату
        sessionStorage.setItem("mono_invoice_id", payData.invoiceId);
        sessionStorage.setItem("mono_order_id", String(newOrderId));

        clearCart(); // очищаємо ДО редиректу
        window.location.href = payData.pageUrl;
        return;
      }

      if (form.paymentMethod === "prepayment" || form.paymentMethod === "fop") {
        setShowPaymentModal(true);
        return;
      }

      clearCart();
      setSuccess(true);
    } catch (err) {
      showToast(
        err.response?.data?.message || err.message || "Помилка при оформленні замовлення",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    clearCart();
    setSuccess(true);
  };

  if (cart.length === 0 && !success) {
    return (
      <div className="checkout-empty">
        <SEO
          title="Кошик порожній"
          description="Декор та фотошпалери ручної роботи в Україні"
          url="https://zori.place/checkout"
        />
        <div className="container">
          <div className="checkout-empty-inner">
            <span>🛒</span>
            <h2>Кошик порожній</h2>
            <p>Додайте товари з каталогу, щоб оформити замовлення</p>
            <Link to="/catalog" className="btn-primary">
              До каталогу
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="checkout-success page-enter">
        <SEO
          title="Дякуємо за замовлення"
          description="Декор та фотошпалери ручної роботи в Україні"
          url="https://zori.place/checkout"
        />
        <div className="container">
          <div className="success-inner">
            <div className="success-icon-wrap">✓</div>
            <h2>Дякуємо за замовлення!</h2>
            <p>Ваше замовлення прийнято.</p>
            <p>
              Номер замовлення: <strong>{orderId}</strong>
            </p>
            <p>
              Ми надіслали вам підтвердження на пошту. <strong>{form.email}</strong> та повідомимо
              про відправлення.
            </p>
            <div className="success-actions">
              <Link to="/" className="btn-primary">
                На головну
              </Link>
              <Link to="/catalog" className="btn-outline">
                Продовжити покупки
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page page-enter">
      <SEO
        title="Оформлення замовлення"
        description="Zori.place — фотошпалери, наліпки та дерев'яний декор ручної роботи з 2019 року. Виготовлення 1–3 дні, доставка по всій Україні."
        url="https://zori.place/"
      />
      <div className="container">
        <h1 className="checkout-title">Оформлення замовлення</h1>

        <div className="checkout-layout">
          <form className="checkout-form" onSubmit={handleSubmit} noValidate>
            {/* Contact */}
            <div className="form-section">
              <h2 className="form-section-title">
                <span className="form-section-num">1</span>
                Контактні дані
              </h2>

              <div className={`form-group ${errors.customerName ? "error" : ""}`}>
                <label htmlFor="customerName">Ім'я та прізвище *</label>
                <input
                  id="customerName"
                  name="customerName"
                  type="text"
                  placeholder="Ваше ім'я та прізвище"
                  value={form.customerName}
                  onChange={handleChange}
                />
                {errors.customerName && <span className="form-error">{errors.customerName}</span>}
              </div>

              <div className="form-row-2">
                <div className={`form-group ${errors.phone ? "error" : ""}`}>
                  <label htmlFor="phone">Телефон *</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+38 (050) 000-00-00"
                    value={form.phone}
                    onChange={handleChange}
                  />
                  {errors.phone && <span className="form-error">{errors.phone}</span>}
                </div>
                <div className={`form-group ${errors.email ? "error" : ""}`}>
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={handleChange}
                  />
                  {errors.email && <span className="form-error">{errors.email}</span>}
                </div>
              </div>
            </div>

            {/* Delivery */}
            <div className="form-section">
              <h2 className="form-section-title">
                <span className="form-section-num">2</span>
                Доставка
              </h2>

              <div className="delivery-options">
                <h3>Спосіб доставки</h3>

                <label className="delivery-card">
                  <input
                    type="radio"
                    name="delivery"
                    checked={form.delivery.provider === "nova"}
                    onChange={() => updateForm("delivery.provider", "nova")}
                  />

                  <div className="delivery-logo">
                    <img src={npImg} alt="nova poshta" />
                  </div>

                  <div className="delivery-content">
                    <div className="delivery-title">Нова Пошта</div>
                  </div>
                </label>

                <label className="delivery-card">
                  <input
                    type="radio"
                    name="delivery"
                    checked={form.delivery.provider === "ukr"}
                    onChange={() => updateForm("delivery.provider", "ukr")}
                  />

                  <div className="delivery-logo">
                    <img src={ukrpImg1} alt="ukr poshta" />
                  </div>

                  <div className="delivery-content">
                    <div className="delivery-title">Укрпошта</div>

                    <div className="delivery-note">Відправка лише при 100% оплаті замовлення</div>
                  </div>
                </label>

                <label className="delivery-card">
                  <input
                    type="radio"
                    name="delivery"
                    checked={form.delivery.provider === "pickup"}
                    onChange={() => {
                      updateForm("delivery.provider", "pickup");
                      updateForm("delivery.city", {
                        id: "pickup",
                        name: "м. Дніпро",
                      });
                      updateForm("delivery.branch", {
                        id: "pickup",
                        name: "просп. Дмитра Яворницького, 93",
                      });
                      updateForm(
                        "delivery.addressText",
                        "м. Дніпро, просп. Дмитра Яворницького, 93"
                      );
                    }}
                  />

                  <div className="delivery-logo">📍</div>

                  <div className="delivery-content">
                    <div className="delivery-title">Самовивіз</div>

                    <div className="delivery-note">м. Дніпро, просп. Дмитра Яворницького, 93</div>
                  </div>
                </label>

                <div className="pickup-info">
                  Самовивіз здійснюється за попередньою домовленістю в місті Дніпро, просп. Дмитра
                  Яворницького, 93
                </div>
              </div>

              {form.delivery.provider !== "pickup" && (
                <div className="form-group">
                  <label>Місто *</label>

                  <CitySelect
                    provider={form.delivery.provider}
                    onSelect={(city) => {
                      updateForm("delivery.city", {
                        id: city.id,
                        name: city.name,
                      });

                      updateForm("delivery.branch", null);
                    }}
                  />
                  {errors.city && <span className="form-error">{errors.city}</span>}
                </div>
              )}

              {form.delivery.provider === "nova" && form.delivery.city && (
                <div className="form-group">
                  <label>Відділення *</label>

                  <BranchSelect
                    provider={form.delivery.provider}
                    city={form.delivery.city}
                    onSelect={(branch) => updateForm("delivery.branch", branch)}
                  />
                  {errors.branch && <span className="form-error">{errors.branch}</span>}
                </div>
              )}

              {form.delivery.provider === "ukr" && (
                <div className={`form-group ${errors.addressText ? "error" : ""}`}>
                  <label>Адреса доставки *</label>

                  <textarea
                    placeholder="Вкажіть повну адресу (вулиця, будинок, квартира, місто)"
                    value={form.delivery.addressText}
                    onChange={(e) => updateForm("delivery.addressText", e.target.value)}
                    rows={3}
                  />

                  {errors.addressText && <span className="form-error">{errors.addressText}</span>}
                </div>
              )}

              {form.delivery.provider !== "pickup" && (
                <div className="form-group">
                  <label>Адреса доставки (якщо кур'єр / додаткова информація)</label>

                  <textarea
                    value={form.delivery.addressText}
                    onChange={(e) => updateForm("delivery.addressText", e.target.value)}
                    rows={2}
                  />
                </div>
              )}
            </div>

            <div className="payment-options">
              <h3>Спосіб оплати</h3>

              {/* <label className="delivery-card">
                <input
                  type="radio"
                  name="payment"
                  checked={form.paymentMethod === "online"}
                  onChange={() => updateForm("paymentMethod", "online")}
                />
                <div className="delivery-logo">💳</div>
                <div className="delivery-content">
                  <div className="delivery-title">Оплата на сайті</div>
                  <div className="delivery-note">Оплата картою онлайн · 100% оплата товару</div>
                </div>
              </label> */}

              <label className="delivery-card">
                <input
                  type="radio"
                  name="payment"
                  checked={form.paymentMethod === "prepayment"}
                  onChange={() => updateForm("paymentMethod", "prepayment")}
                />

                <div className="delivery-logo">💰</div>

                <div className="delivery-content">
                  <div className="delivery-title">Передоплата</div>

                  <div className="delivery-note">
                    Фотошпалери — 100% оплата
                    <br />
                    Індивідуальне — 50% від вартості
                    <br />
                    Стандартні набори — мінімум 200 грн
                    <br />
                    Відправка Укрпоштою лише по 100% оплаті
                    <br />
                    Наш менеджер зв'яжеться з Вами для подальшого оформлення замовлення.
                  </div>
                </div>
              </label>

              <label className="delivery-card">
                <input
                  type="radio"
                  name="payment"
                  checked={form.paymentMethod === "fop"}
                  onChange={() => updateForm("paymentMethod", "fop")}
                />

                <div className="delivery-logo">🏦</div>

                <div className="delivery-content">
                  <div className="delivery-title">Оплата на реквізити ФОП</div>
                </div>
              </label>
            </div>

            {/* Certificate */}
            <div className="form-section">
              <h2 className="form-section-title">
                <span className="form-section-num">3</span>
                Подарунковий сертифікат
              </h2>
              <div className="cert-input-row">
                <input
                  type="text"
                  placeholder="Введіть код сертифіката (напр. ZP-AB12CD34)"
                  value={certCode}
                  onChange={(e) => {
                    setCertCode(e.target.value);
                    setCertError("");
                    setCertData(null);
                  }}
                  className="cert-input"
                  style={{ textTransform: "uppercase" }}
                />
                <button
                  type="button"
                  className="btn-outline cert-apply-btn"
                  onClick={handleApplyCert}
                  disabled={certLoading || !certCode.trim()}
                >
                  {certLoading ? "..." : "Застосувати"}
                </button>
              </div>
              {certError && (
                <p className="form-error" style={{ marginTop: 6 }}>
                  {certError}
                </p>
              )}
              {certData && (
                <div className="cert-applied">
                  <span>🎁</span>
                  <div>
                    <strong>Сертифікат {certData.code} застосовано</strong>
                    <p>Знижка: {discount.toFixed(0)} ₴</p>
                  </div>
                </div>
              )}
            </div>
            <button type="submit" className="btn-primary submit-btn" disabled={submitting}>
              {submitting
                ? "Оформлення..."
                : form.paymentMethod === "online"
                  ? `Оплатити ${finalPrice.toFixed(0)} ₴`
                  : `Підтвердити замовлення — ${finalPrice.toFixed(0)} ₴`}
            </button>
          </form>

          {/* Summary */}
          <div className="checkout-order-summary">
            <h2>Ваше замовлення</h2>
            <div className="checkout-items">
              {cart.map((item) => {
                const imgUrl = item.images[0]
                  ? item.images[0].startsWith("http")
                    ? item.images[0]
                    : `${item.images[0]}`
                  : null;
                return (
                  <div
                    key={`${item.id}-${item.selectedSize?.id}-${item.selectedTexture?.id}`}
                    className="checkout-item"
                  >
                    <div className="checkout-item-image">
                      {imgUrl ? <img src={imgUrl} alt={item.name} /> : <span>✿</span>}
                    </div>
                    <div className="checkout-item-info">
                      <p>{item.name}</p>
                      <span>× {item.quantity}</span>
                    </div>
                    <div className="checkout-item-price">
                      {(Number(item.unitPrice) * item.quantity).toFixed(0)} ₴
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="checkout-summary-lines">
              <div className="summary-line">
                <span>Сума товарів</span>
                <span>{totalPrice.toFixed(0)} ₴</span>
              </div>
              {discount > 0 && (
                <div className="summary-line discount-line">
                  <span>🎁 Сертифікат</span>
                  <span>−{discount.toFixed(0)} ₴</span>
                </div>
              )}
            </div>

            <div className="checkout-total">
              <span>До оплати</span>
              <span>{finalPrice.toFixed(0)} ₴</span>
            </div>

            <div className="checkout-trust">
              <div>
                <span>🚚</span> Доставка Нова Пошта / Укрпошта
              </div>
              <div>
                <span>✅</span> Виготовлення 1–3 робочих дні
              </div>
              <div>
                <span>💳</span> Оплата при отриманні або онлайн
              </div>
            </div>
          </div>
        </div>
      </div>
      {showPaymentModal && (
        <div className="modal-overlay">
          <div className="payment-modal">
            <h3>Реквізити для оплати</h3>

            <p>
              <strong>Отримувач:</strong> ФОП Давиденко Олександра Миколаївна
            </p>

            <p>
              <strong>Банк:</strong> АТ «Універсал Банк»
            </p>

            <p>
              <strong>Код отримувача:</strong> 2867006367
            </p>

            <p>
              <strong>IBAN:</strong>
              <br />
              UA563220010000026007350020921
            </p>

            <p>
              <strong>Призначення:</strong>
              <br />
              Оплата за товар від {form.customerName}
            </p>

            <button className="btn-primary" onClick={closePaymentModal}>
              Зрозуміло
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
